import './TicketShowDetail.scss';

import { TextField } from '@refinedev/antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import FormattedTextInput from '@/components/general/FormattedTextInput';
import LinkTextField from '@/components/general/LinkTextField';
import PriorityLabel from '@/components/general/PriorityLabel';
import UploadedFileCarousel from '@/components/general/UploadFile/UploadedFileCarousel';
import { COMPANY_URLS, MACHINES_URLS } from '@/constants/urls';
import FileData from '@/model/General/FileData';
import { Ticket } from '@/model/Ticket/Ticket';
import { TicketTypeString } from '@/model/Ticket/TicketConstants';
import { ensureHtml } from '@/utils/ensureHtml';

interface TicketShowDataProps {
  record?: Ticket;
  subcategory?: any;
  empresa?: any;
  machine?: any;
  reference?: any;
  user?: any;
  substatus?: any;
}

const TicketShowDetail = (props: TicketShowDataProps) => {
  const { t } = useTranslation();
  const { record, subcategory, empresa, machine, reference, user, substatus } = props;
  const fileList = useMemo(() => record?.files?.map(files => new FileData(files)), [record?.files]);
  const descriptionHtml = useMemo(() => ensureHtml(record?.description), [record?.description]);

  return (
    <div className="TicketShowDetail">
      {/* Subcat y observacion */}
      <div className="TicketShowRow">
        <div className="TicketShowData">
          <b>{t('Subcategoría')}</b>
          <TextField value={subcategory?.title_str} />
        </div>
        <div className="TicketShowData">
          <b>{t('Título')}</b>
          <TextField value={record?.ticket_title_str} />
        </div>
      </div>
      {/* Empresa y movil */}
      <div className="TicketShowRow">
        <div className="TicketShowData">
          <b>{t('Empresa')}</b>
          {record?.empresa ? (
            <LinkTextField
              label={`${empresa?.full_name} (${empresa?.id})`}
              to={`${COMPANY_URLS.ADMIN_EMPRESA}/${empresa?.id}`}
              admin
            />
          ) : (
            '-'
          )}
        </div>
        <div className="TicketShowData">
          <b>{t('Móvil')}</b>
          {machine ? (
            <LinkTextField
              label={`${machine?.description} (${machine?.id})`}
              to={`${MACHINES_URLS.ADMIN_MOVIL}/${machine?.id}`}
              admin
            />
          ) : (
            '-'
          )}
        </div>
      </div>
      {/* Asignado y referencia */}
      <div className="TicketShowRow">
        <div className="TicketShowData">
          <b>{t('Asignado a')}</b>
          <TextField
            value={user ? `${user?.first_name} ${user?.last_name} (${user?.username})` : '-'}
          />
        </div>
        <div className="TicketShowData">
          <b>{t('Prioridad')}</b>
          <TextField value={record?.priority ? <PriorityLabel value={record?.priority} /> : '-'} />
        </div>
      </div>
      {/* Estado y subestado */}
      <div className="TicketShowRow">
        <div className="TicketShowData">
          <b>{t('Subestado')}</b>
          <TextField value={t(substatus?.title ?? '-')} />
        </div>
        <div className="TicketShowData">
          <b>{t('Referencia')}</b>
          {reference ? (
            <LinkTextField
              label={`${reference?.id} - ${reference?.ticket_title_str}`}
              to={'/tickets/show/' + record?.referencia}
              local
            />
          ) : (
            '-'
          )}
        </div>
      </div>
      {/* Prioridad y tipo */}
      <div className="TicketShowRow">
        <div className="TicketShowData">
          <b>{t('Tipo de contacto')}</b>
          {record?.contact_type ? (
            <TextField value={t(TicketTypeString[record?.contact_type])} />
          ) : (
            '-'
          )}
        </div>
      </div>

      {/* Links adicionales */}
      {record?.movil && (
        <div className="TicketShowRow">
          <div className="TicketShowData">
            <b>{t('Links adicionales de máquina')}</b>
            <div className="TicketShowDataLinks">
              <LinkTextField
                label={t('Info de móvil')}
                to={`/machines/detail/${record?.movil}`}
                platform
              />
              <LinkTextField
                label={t('Ver historial de trabajos')}
                to={`/machines/works/${record?.movil}`}
                platform
              />
              <LinkTextField
                label={t('Ver historial por hora')}
                to={`/machines/history/${record?.movil}`}
                platform
              />
            </div>
          </div>
        </div>
      )}

      <div className="TicketShowRow">
        <div className="TicketShowData">
          <b>{t('Descripción')}</b>
          {record?.description ? <FormattedTextInput value={descriptionHtml} readOnly /> : '-'}
        </div>
      </div>

      {/* Archivos subidos */}
      {fileList && fileList.length > 0 && (
        <div className="TicketShowRow">
          <div className="TicketShowData">
            <b>{t('Archivos subidos')}</b>
            <UploadedFileCarousel fileList={fileList} />
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketShowDetail;
